import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  SearchBarWithFilters,
  Button,
  ButtonsWrapper,
} from "components/elements";
import { PageContent, PageHeader, Section, TableBody } from "components/layout";
import { RestrictedArea, ROLES } from "containers/Permissions";
import { TabbedTable } from "containers/TabbedTable";
import { useProviders, useProvidersFilters } from "hooks/tivoApps";
import { TiVoAppProvider } from "hooks/tivoApps/types";
import { createLocationWithSearch } from "utils/url";

import { useFiltersSuggestions } from "../_shared";
import { MAIN_ROUTE, PAGE_LIMIT } from "../constants";
import { parseLocationToQueryParams } from "../utils";
import { TiVoAppRow } from "./components/TiVoAppRow";
import { TEXTS, TABS_CONFIG } from "./constants";

export function TiVoAppsList() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: filters } = useProvidersFilters();
  const { data, isLoading, error } = useProviders({
    ...parseLocationToQueryParams(location),
    include: ["variantDraft", "variants"],
  });
  const { fetchSuggestions } = useFiltersSuggestions();
  const availableFilters = useMemo(
    () => ({
      sections: [
        {
          title: "Filters",
          options: filters?.results ?? [],
        },
      ],
    }),
    [filters?.results]
  );

  const handleSearch = useCallback(
    (search: Record<string, any>) =>
      navigate(createLocationWithSearch(location, search)),
    [location, navigate]
  );

  const renderTableBody = (providers: TiVoAppProvider[]) => {
    return (
      <TableBody>
        {providers.map((provider: TiVoAppProvider) => (
          <TiVoAppRow key={provider.providerId} provider={provider} />
        ))}
      </TableBody>
    );
  };

  return (
    <>
      <PageHeader>{TEXTS.HEADER}</PageHeader>
      <PageContent>
        <Section>
          <SearchBarWithFilters
            filtersDataChoices={availableFilters}
            placeholder={TEXTS.SEARCH_BAR_PLACEHOLDER}
            onSearch={handleSearch}
            suggestionApiEndpoint={fetchSuggestions}
          />
        </Section>
        <RestrictedArea allowed={[ROLES.tivo.provider]} showFallback={false}>
          <ButtonsWrapper>
            <Button
              to={`/${MAIN_ROUTE}/create`}
              type={"green"}
              dataTestId="create-provider-button"
            >
              {TEXTS.CREATE_BTN}
            </Button>
          </ButtonsWrapper>
        </RestrictedArea>
        <TabbedTable
          data={{
            loading: isLoading,
            results: data?.results || [],
            count: data?.meta?.total,
            error: error,
            defaultOrder: {
              sortBy: "name",
              sortOrder: "ASC",
            },
          }}
          renderTableBody={renderTableBody}
          rowsPerPage={PAGE_LIMIT}
          tabsConfig={TABS_CONFIG}
          layout={"fixed"}
        />
      </PageContent>
    </>
  );
}
